/* eslint-disable react-refresh/only-export-components */
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";

import { AdminIcon } from "@/components/admin/icon";

// ─── Types ──────────────────────────────────────────────────────────────────
export interface ConfirmOptions {
	title: string;
	message?: ReactNode;
	confirmLabel?: string;
	cancelLabel?: string;
	tone?: "danger" | "default";
	/** Si défini, l'utilisateur doit saisir exactement ce texte pour activer le bouton. */
	requireText?: string;
	/** Libellé affiché au-dessus du champ de saisie (défaut : « Tapez … pour confirmer »). */
	requireHint?: ReactNode;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

type ToastVariant = "success" | "error" | "info";
interface ToastItem {
	id: number;
	message: string;
	variant: ToastVariant;
}
export interface ToastApi {
	(message: string): void;
	success: (message: string) => void;
	error: (message: string) => void;
	info: (message: string) => void;
}

interface FeedbackCtx {
	confirm: ConfirmFn;
	toast: ToastApi;
}

const Ctx = createContext<FeedbackCtx | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────
interface ActiveConfirm extends ConfirmOptions {
	resolve: (ok: boolean) => void;
}

export function UiProvider({ children }: { children: ReactNode }) {
	const [active, setActive] = useState<ActiveConfirm | null>(null);
	const [toasts, setToasts] = useState<ToastItem[]>([]);
	const toastSeq = useRef(0);

	const confirm = useCallback<ConfirmFn>((opts) => {
		return new Promise<boolean>((resolve) => {
			setActive({ ...opts, resolve });
		});
	}, []);

	const closeConfirm = useCallback((ok: boolean) => {
		setActive((cur) => {
			cur?.resolve(ok);
			return null;
		});
	}, []);

	const removeToast = useCallback((id: number) => {
		setToasts((list) => list.filter((t) => t.id !== id));
	}, []);

	const pushToast = useCallback(
		(message: string, variant: ToastVariant) => {
			const id = ++toastSeq.current;
			setToasts((list) => [...list, { id, message, variant }]);
			window.setTimeout(() => removeToast(id), 5000);
		},
		[removeToast],
	);

	const toast = useCallback(
		((message: string) => pushToast(message, "info")) as ToastApi,
		[pushToast],
	);
	toast.success = useCallback(
		(m: string) => pushToast(m, "success"),
		[pushToast],
	);
	toast.error = useCallback((m: string) => pushToast(m, "error"), [pushToast]);
	toast.info = useCallback((m: string) => pushToast(m, "info"), [pushToast]);

	return (
		<Ctx.Provider value={{ confirm, toast }}>
			{children}
			{active && <ConfirmDialog {...active} onResolve={closeConfirm} />}
			<Toaster toasts={toasts} onClose={removeToast} />
		</Ctx.Provider>
	);
}

// ─── Hooks ──────────────────────────────────────────────────────────────────
export function useConfirm(): ConfirmFn {
	const ctx = useContext(Ctx);
	if (!ctx) throw new Error("useConfirm doit être utilisé dans <UiProvider>");
	return ctx.confirm;
}

export function useToast(): ToastApi {
	const ctx = useContext(Ctx);
	if (!ctx) throw new Error("useToast doit être utilisé dans <UiProvider>");
	return ctx.toast;
}

// ─── Modal de confirmation ──────────────────────────────────────────────────
function ConfirmDialog({
	title,
	message,
	confirmLabel = "Confirmer",
	cancelLabel = "Annuler",
	tone = "danger",
	requireText,
	requireHint,
	onResolve,
}: ConfirmOptions & { onResolve: (ok: boolean) => void }) {
	const [value, setValue] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);
	const canConfirm = !requireText || value.trim() === requireText.trim();

	useEffect(() => {
		const t = window.setTimeout(() => inputRef.current?.focus(), 30);
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onResolve(false);
		};
		window.addEventListener("keydown", onKey);
		return () => {
			window.clearTimeout(t);
			window.removeEventListener("keydown", onKey);
		};
	}, [onResolve]);

	const danger = tone === "danger";

	return createPortal(
		// biome-ignore lint/a11y/noStaticElementInteractions: backdrop overlay click
		// biome-ignore lint/a11y/useKeyWithClickEvents: backdrop overlay click
		<div
			role="dialog"
			aria-modal="true"
			onClick={() => onResolve(false)}
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 1000,
				background: "rgba(20,16,12,0.45)",
				display: "grid",
				placeItems: "center",
				padding: 16,
			}}
		>
			{/* biome-ignore lint/a11y/noStaticElementInteractions: dialog box container */}
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: dialog box container */}
			<div
				onClick={(e) => e.stopPropagation()}
				style={{
					width: "min(440px, 100%)",
					background: "var(--a-surface)",
					border: "1px solid var(--a-border)",
					borderRadius: "var(--a-r-lg, 12px)",
					boxShadow: "0 24px 60px -12px rgba(0,0,0,0.35)",
					overflow: "hidden",
				}}
			>
				<div style={{ display: "flex", gap: 12, padding: "18px 18px 0" }}>
					<div
						style={{
							width: 36,
							height: 36,
							flexShrink: 0,
							borderRadius: "50%",
							display: "grid",
							placeItems: "center",
							background: danger
								? "var(--a-danger-soft, #fdecea)"
								: "var(--a-surface-2)",
							color: danger ? "var(--a-danger)" : "var(--a-ink)",
						}}
					>
						<AdminIcon n={danger ? "alert" : "info"} s={18} />
					</div>
					<div style={{ flex: 1, paddingTop: 2 }}>
						<h3
							style={{
								margin: 0,
								fontSize: 16,
								fontWeight: 700,
								color: "var(--a-ink)",
							}}
						>
							{title}
						</h3>
						{message && (
							<div
								style={{
									marginTop: 6,
									fontSize: 13,
									color: "var(--a-ink-soft)",
									lineHeight: 1.5,
								}}
							>
								{message}
							</div>
						)}
					</div>
				</div>

				{requireText && (
					<div style={{ padding: "14px 18px 0" }}>
						<label
							htmlFor="confirm-text-input"
							style={{
								display: "block",
								fontSize: 12,
								color: "var(--a-ink-soft)",
								marginBottom: 6,
							}}
						>
							{requireHint ?? (
								<>
									Tapez{" "}
									<strong style={{ color: "var(--a-ink)", userSelect: "all" }}>
										{requireText}
									</strong>{" "}
									pour confirmer
								</>
							)}
						</label>
						<input
							id="confirm-text-input"
							ref={inputRef}
							value={value}
							onChange={(e) => setValue(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && canConfirm) onResolve(true);
							}}
							className="aform-i"
							placeholder={requireText}
							autoComplete="off"
							spellCheck={false}
							style={{ width: "100%" }}
						/>
					</div>
				)}

				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
						padding: 18,
						marginTop: 6,
					}}
				>
					<button
						type="button"
						className="btn btn-ghost"
						onClick={() => onResolve(false)}
					>
						{cancelLabel}
					</button>
					<button
						type="button"
						className="btn"
						disabled={!canConfirm}
						onClick={() => onResolve(true)}
						ref={!requireText ? (el) => el?.focus() : undefined}
						style={{
							color: "#fff",
							background: danger ? "var(--a-danger)" : "var(--a-terracotta)",
							opacity: canConfirm ? 1 : 0.5,
							cursor: canConfirm ? "pointer" : "not-allowed",
						}}
					>
						{confirmLabel}
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
}

// ─── Toasts ─────────────────────────────────────────────────────────────────
const TOAST_STYLES: Record<
	ToastVariant,
	{ bg: string; fg: string; icon: "check" | "alert" | "info" }
> = {
	success: {
		bg: "var(--a-surface)",
		fg: "var(--a-success, #1a7f5a)",
		icon: "check",
	},
	error: { bg: "var(--a-surface)", fg: "var(--a-danger)", icon: "alert" },
	info: { bg: "var(--a-surface)", fg: "var(--a-ink)", icon: "info" },
};

function Toaster({
	toasts,
	onClose,
}: {
	toasts: ToastItem[];
	onClose: (id: number) => void;
}) {
	if (toasts.length === 0) return null;
	return createPortal(
		<div
			style={{
				position: "fixed",
				bottom: 20,
				right: 20,
				zIndex: 1100,
				display: "flex",
				flexDirection: "column",
				gap: 8,
				maxWidth: 380,
			}}
		>
			{toasts.map((t) => {
				const s = TOAST_STYLES[t.variant];
				return (
					<div
						key={t.id}
						role="status"
						style={{
							display: "flex",
							alignItems: "flex-start",
							gap: 10,
							padding: "11px 13px",
							background: s.bg,
							border: "1px solid var(--a-border)",
							borderLeft: `3px solid ${s.fg}`,
							borderRadius: 8,
							boxShadow: "0 10px 28px -8px rgba(0,0,0,0.22)",
							fontSize: 13,
							color: "var(--a-ink)",
						}}
					>
						<span style={{ color: s.fg, marginTop: 1, flexShrink: 0 }}>
							<AdminIcon n={s.icon} s={15} />
						</span>
						<span style={{ flex: 1, lineHeight: 1.45 }}>{t.message}</span>
						<button
							type="button"
							onClick={() => onClose(t.id)}
							aria-label="Fermer"
							style={{
								opacity: 0.5,
								cursor: "pointer",
								flexShrink: 0,
								marginTop: 1,
							}}
						>
							<AdminIcon n="close" s={12} />
						</button>
					</div>
				);
			})}
		</div>,
		document.body,
	);
}
